
import React from "react"


import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { useAuth } from "./useAuth"

type CommonAreaRule = {
  id: string
  condominium_id: string
  rule_text: string
  created_by: string
  created_at: string
  updated_at: string
  is_active: boolean
}

type CreateRuleData = {
  rule_text: string
}

export const useCommonAreaRules = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["common-area-rules", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return []

      const { data, error } = await supabase
        .from("common_area_rules")
        .select("*")
        .eq("condominium_id", profile.condominium_id)
        .eq("is_active", true)
        .order("created_at", { ascending: true })

      if (error) throw error
      return data as CommonAreaRule[]
    },
    enabled: !!profile?.condominium_id
  })
}

export const useCreateRule = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (data: CreateRuleData) => {
      if (!profile?.condominium_id) throw new Error("Usuário sem condomínio associado")

      const { data: rule, error } = await supabase
        .from("common_area_rules")
        .insert({
          rule_text: data.rule_text,
          condominium_id: profile.condominium_id,
          created_by: profile.id
        })
        .select()
        .single()

      if (error) throw error
      return rule
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["common-area-rules"] })
      toast.success("Regra adicionada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao criar regra:", error)
      toast.error("Erro ao adicionar regra")
    }
  })
}

export const useUpdateRule = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, rule_text }: { id: string; rule_text: string }) => {
      const { data, error } = await supabase
        .from("common_area_rules")
        .update({ rule_text })
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["common-area-rules"] })
      toast.success("Regra atualizada com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar regra:", error)
      toast.error("Erro ao atualizar regra")
    }
  })
}

export const useDeleteRule = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("common_area_rules")
        .update({ is_active: false })
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["common-area-rules"] })
      toast.success("Regra removida com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao remover regra:", error)
      toast.error("Erro ao remover regra")
    }
  })
}
